<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <Form ref="questionForm" @submit="addQuestion" :validation-schema="questionSchema">
        <div class="row">
          <!-- Première ligne : Support de cours, Module -->
          <div class="col-md-6">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Support de cours <span class="text-danger">*</span>
              </label>
              <Field name="supportDeCours" v-model="formData.supportDeCours" v-slot="{ field }">
                <Multiselect
                  :options="supportDeCoursOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-model="formData.supportDeCours"
                  @change="onSupportChange"
                  placeholder="Sélectionner un support de cours"
                  :disabled="isLoadingSupports || supportDisabled"
                  v-bind="field"
                />
              </Field>
              <ErrorMessage name="supportDeCours" class="text-danger" />
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Module
              </label>
              <Field name="module" v-slot="{ field }">
                <Multiselect
                  :key="formData.supportDeCours"
                  :options="moduleOptions"
                  :searchable="true"
                  track-by="value"
                  label="label"
                  v-model="formData.module"
                  v-bind="field"
                  placeholder="Sélectionner un module (facultatif)"
                  :disabled="!formData.supportDeCours || isLoadingModules"
                />
              </Field>
              <ErrorMessage name="module" class="text-danger" />
              <small v-if="isLoadingModules" class="text-info">Chargement des modules...</small>
            </div>
          </div>
        </div>
        <!-- Deuxième ligne : Contenu -->
        <div class="row">
          <div class="col-md-12">
            <div class="form-group mb-10">
              <label class="d-block text-black fw-semibold mb-10">
                Contenu <span class="text-danger">*</span>
              </label>
              <Field
                name="contenu"
                type="text"
                class="form-control shadow-none text-black"
                placeholder="Entrer le contenu"
                v-model="formData.contenu"
              />
              <ErrorMessage name="contenu" class="text-danger" />
            </div>
          </div>
        </div>
        <!-- Section des propositions -->
        <div class="border border-primary mb-10">
          <div class="row d-flex align-items-center justify-content-between fw-bold py-2 bg-primary">
            <div class="col-md-7">
              <h4 class="fs-4 text-white">Ajouter les propositions</h4>
            </div>
            <div class="col-md-5">
              <div class="d-flex float-end">
                <button
                  class="btn btn-success"
                  type="button"
                  @click="addProposition"
                >
                  <i class="fa fa-plus-circle position-relative me-1 fs-12"></i>
                  Ajouter une proposition
                </button>
              </div>
            </div>
          </div>
          <div class="marge-droite">
            <div v-for="(proposition, index) in propositions" :key="index">
              <div class="row mt-10">
                <div class="col-md-8">
                  <label class="d-block text-black fw-semibold mb-10">
                    Contenu <span class="text-danger">*</span>
                  </label>
                </div>
                <div class="col-md-2 text-center">
                  <label class="d-block text-black fw-semibold mb-10">Correcte</label>
                </div>
                <div class="col-md-2 text-center">
                  <label class="d-block text-black fw-semibold mb-10">Action</label>
                </div>
              </div>
              <div class="row">
                <div class="col-md-8">
                  <div class="form-group mb-10">
                    <Field
                      :name="`propositions[${index}].contenu`"
                      type="text"
                      :class="[
                        validateField(proposition.contenu)
                          ? 'form-control shadow-none text-black is-invalid'
                          : 'form-control shadow-none text-black',
                      ]"
                      placeholder="Entrer le contenu de la proposition"
                      v-model="proposition.contenu"
                    />
                    <ErrorMessage :name="`propositions[${index}].contenu`" class="text-danger" />
                  </div>
                </div>
                <div class="col-md-2 d-flex justify-content-center align-items-start pt-2">
                  <input
                    type="checkbox"
                    class="form-check-input"
                    v-model="proposition.estLaBonneReponse"
                    :true-value="true"
                    :false-value="false"
                  />
                </div>
                <div class="col-md-2 d-flex justify-content-center align-items-start pt-1">
                  <button
                    class="btn btn-danger transition border-0 pb-2 ps-8 pe-8"
                    type="button"
                    @click="removeProposition(index)"
                    v-if="propositions.length > 1"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                      class="text-white"
                    >
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                      <path fill-rule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <hr class="mt-0" />
        </div>
        <div class="col-md-12 mt-5">
          <div class="d-flex align-items-center">
            <button
              class="btn btn-success me-3"
              type="submit"
              :disabled="isDisable"
              :class="{ 'cursor-not-allowed': isDisable }"
            >
              Ajouter une question
            </button>
            <router-link
              to="/liste-questions"
              class="btn btn-danger"
            >
              <i class="fa fa-trash-o lh-1 me-1 position-relative top-2"></i>
              Annuler
            </router-link>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, reactive, computed, watch, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import ApiService from '@/services/ApiService';
import { success, error } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import { useRouter } from 'vue-router';


export default defineComponent({
  name: 'AddQuestionQCM',
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  props: {
    currentSupportId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props, { emit }) {
    const router = useRouter();
    const questionForm = ref<InstanceType<typeof Form> | null>(null);
    
    const formData = reactive({
      contenu: '',
      supportDeCours: '', 
      module: '',
    });
    const propositions = reactive([
      { contenu: '', estLaBonneReponse: false },
    ]);
    const isLoadingModules = ref(false);
    
    const questionSchema = Yup.object().shape({
      contenu: Yup.string().required('Le contenu est obligatoire'),
      supportDeCours: Yup.string().required('Le support de cours est obligatoire'),
      module: Yup.string().nullable().notRequired(),
    });
  
    const supportDeCoursOptions = ref<Array<{ value: string; label: string }>>([]);
    const moduleOptions = ref<Array<{ value: string; label: string }>>([]);
     onMounted(async () => {
      await loadSupportsDeCours();
    });
    const isLoadingSupports = ref(false);
    const supportDisabled = computed(() => !!props.currentSupportId);
    
    // Watch pour pré-sélectionner quand currentSupportId arrive
    watch(
      () => props.currentSupportId,
      async (newSupportId) => {
        if (newSupportId) {
          await loadSupportsDeCours();
          console.log('Pré-sélection du support:', newSupportId, supportDeCoursOptions);
        const supportFound = supportDeCoursOptions.value.find(
            (o) => o.value == newSupportId
          );
         // console.log('Pré-sélection du supportsupportFound :', supportFound);
         // if (supportFound) {
            formData.supportDeCours = String(newSupportId);
            await loadModules();
         // }
        }
      }
    );
   
    const validateField = (value: string) => {
      return !value || value.trim() === '';
    };
   
    const isDisable = computed(() => {
      const mainFieldsValid = formData.contenu && formData.supportDeCours;
      const propositionsValid = propositions.length > 0 && propositions.every(
        (prop) => prop.contenu && prop.contenu.trim() !== ''
      );
      return !(mainFieldsValid && propositionsValid);
    });
    
    const loadSupportsDeCours = async () => {
      try {
        isLoadingSupports.value = true;
        const { data } = await ApiService.get('/supports');
        console.log('Supports de cours chargés:', data);
        supportDeCoursOptions.value = data.data.data.map((support: any) => ({
          value: support.id.toString(),
          label: support.titre || `${support.filiereNiveauMatiere?.titre || 'N/A'} (${support.nombreHeures}h)`,
        }));
        console.log('Options de supports de cours:', supportDeCoursOptions.value);
      } catch (err: any) {
        const message = err.response?.data?.message || 'Erreur lors du chargement des supports de cours';
        console.error('Erreur loadSupportsDeCours:', err);
        error(message);
      } finally {
        isLoadingSupports.value = false;
      }
    };
    // Chargement des modules selon le support sélectionné
    const loadModules = async () => {
      const supportId = formData.supportDeCours;
      if (!supportId) {
        moduleOptions.value = [];
        formData.module = '';
        return;
      }
      try {
        isLoadingModules.value = true;
        console.log('Chargement des modules pour le support:', supportId);
       
        const { data } = await ApiService.get(`/supports/${supportId}/modules`);
        console.log('Modules reçus:', data);
       
        if (data && data.code === 200 && data.data) {
          const modules = data.data.data || data.data;
         
          if (Array.isArray(modules) && modules.length > 0) {
            moduleOptions.value = modules.map((module: any) => ({
              value: module.id,
              label: module.titre,
            }));
            console.log('Options de modules:', moduleOptions.value);
          } else {
            moduleOptions.value = [];
            console.log('Aucun module trouvé pour ce support');
          }
        } else {
          moduleOptions.value = [];
        }
      } catch (err: any) {
        const message = err.response?.data?.message || 'Erreur lors du chargement des modules';
        console.error('Erreur loadModules:', err);
        error(message);
        moduleOptions.value = [];
        formData.module = '';
      } finally {
        isLoadingModules.value = false;
      }
    };
    
    const onSupportChange = (value: string) => {
      console.log('Support sélectionné:', value);
      formData.supportDeCours = value;
      formData.module = ''; // Réinitialiser le module
      moduleOptions.value = [];

      if (formData.supportDeCours) {
        loadModules(); // Charger les modules du support sélectionné
      }
    };
    
    const addProposition = () => {
      propositions.push({ contenu: '', estLaBonneReponse: false });
    };
    const removeProposition = (index: number) => {
      if (propositions.length > 1) {
        propositions.splice(index, 1);
      }
    };
    
    const addQuestion = async (values: any, { resetForm }: any) => {
      const supportDeCoursValue = formData.supportDeCours;
      const moduleValue = formData.module ? formData.module : null;
      const questionData = {
        contenu: formData.contenu,
        type: 'choix_multiple',
        supportDeCoursId: supportDeCoursValue,
        moduleId: moduleValue,
        propositions: propositions
          .filter((prop) => prop.contenu && prop.contenu.trim() !== '')
          .map((prop) => ({
            contenu: prop.contenu,
            estLaBonneReponse: prop.estLaBonneReponse === true,
          })),
      };
      console.log('Données envoyées au backend:', questionData);
      try {
        const { data } = await ApiService.post('/questions', questionData);
        if (data.code === 201) {
          success(data.message);
          resetForm();
          formData.contenu = '';
          formData.supportDeCours = '';
          formData.module = '';
          moduleOptions.value = [];
          propositions.splice(0, propositions.length, { contenu: '', estLaBonneReponse: false });

          if (supportDeCoursValue) {
             emit('question-added');
          } else {
            router.push({
              name: 'ListeQuestionPage',
            });
          }
        } else {
          error(data.message || 'Erreur lors de la création de la question');
        }
      } catch (err: any) {
        const message = err.response?.data?.message || 'Erreur lors de la création de la question';
        console.error('Erreur addQuestion:', err);
        error(message);
      }
    };
    
   
    return {
      questionSchema,
      questionForm,
      formData,
      propositions,
      supportDeCoursOptions,
      moduleOptions,
      isLoadingModules,
      isLoadingSupports,
      supportDisabled,
      addProposition,
      removeProposition,
      validateField,loadSupportsDeCours,
      isDisable,
      addQuestion,
      loadModules,
      onSupportChange,
    };
  },
});
</script>
<style scoped>
.cursor-not-allowed {
  cursor: not-allowed;
}
.marge-droite {
  margin-inline-start: 87px;
}
.hr-longueur {
  inline-size: 80%;
}
.bord1 {
  border: 1px solid #07a;
}
.fond {
  background-color: rgb(94, 191, 233);
}
</style>